/**
 * OVA motion layer.
 * Progressive enhancement only: without this file every element is visible and usable.
 * - Scroll reveals ([data-ova-reveal], plus product grid items) with stagger
 * - Header "scrolled" state
 * - Pointer spotlight on product cards, 3D tilt ([data-ova-tilt]) and magnetic CTAs ([data-ova-magnetic])
 * - Scroll parallax ([data-ova-parallax="0.15"])
 * - Cart bubble bump when the count changes
 * Respects prefers-reduced-motion, skips pointer effects on touch, and stays idle in the theme editor.
 */
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const designMode = root.classList.contains('shopify-design-mode');

const motionAllowed = () => !reducedMotion.matches && !designMode;

/* ---------- Scroll reveal ---------- */

const AUTO_REVEAL = [
  '#MainContent .product-grid__item',
  '#MainContent .resource-list__item',
  '#MainContent .blog-post-card',
  '.ova-footer [data-ova-reveal-auto]',
];

let revealObserver;

function reveal(element) {
  element.classList.add('is-revealed');
  revealObserver?.unobserve(element);
  // Drop the stagger once the entrance is done so hover reactions are immediate.
  setTimeout(() => element.style.setProperty('--ova-stagger', '0'), 1600);
}

function setupReveal(scope = document) {
  if (!motionAllowed()) {
    scope.querySelectorAll('[data-ova-reveal]').forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  // Tag grid items once, with a stagger that restarts on every row. Items already on screen are
  // left alone: they have been painted and must not blink out.
  scope.querySelectorAll(AUTO_REVEAL.join(',')).forEach((el, index) => {
    if (el.hasAttribute('data-ova-reveal')) return;
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.setAttribute('data-ova-reveal', '');
    el.style.setProperty('--ova-stagger', String(index % 4));
  });

  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) if (entry.isIntersecting) reveal(entry.target);
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  scope.querySelectorAll('[data-ova-reveal]:not(.is-revealed)').forEach((el) => {
    // Anything already on screen at load shows immediately (no flash on the fold).
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0 && document.readyState !== 'loading' && scope === document && !el.closest('[data-ova-intro]')) {
      requestAnimationFrame(() => reveal(el));
    } else {
      revealObserver.observe(el);
    }
  });
}

/* ---------- Header scrolled state + parallax (one rAF loop driven by scroll) ---------- */

let parallaxItems = [];
let ticking = false;

function collectParallax() {
  parallaxItems = motionAllowed()
    ? [...document.querySelectorAll('[data-ova-parallax]')].map((el) => ({
        el,
        speed: parseFloat(el.dataset.ovaParallax) || 0.15,
      }))
    : [];
}

function onScrollFrame() {
  ticking = false;
  document.body.classList.toggle('ova-scrolled', getScrollTop() > 8);

  const vh = window.innerHeight;
  for (const { el, speed } of parallaxItems) {
    const rect = el.parentElement?.getBoundingClientRect() ?? el.getBoundingClientRect();
    if (rect.bottom < -vh || rect.top > vh * 2) continue;
    const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
    el.style.setProperty('--ova-parallax', `${offset.toFixed(1)}px`);
  }
}

function requestScrollFrame() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(onScrollFrame);
}

/* ---------- Pointer effects (fine pointers only) ---------- */

function setupPointer() {
  if (!finePointer.matches || !motionAllowed()) return;

  // At most one layout read/write pass per frame, whatever the pointer event rate.
  let pending = null;
  document.addEventListener(
    'pointermove',
    (event) => {
      if (!pending) requestAnimationFrame(() => {
        const queued = pending;
        pending = null;
        if (queued) applyPointer(queued);
      });
      pending = event;
    },
    { passive: true }
  );

  function applyPointer(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const card = target.closest('.product-card, .ova-card-spot');
    if (card) {
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--ova-spot-x', `${event.clientX - rect.left}px`);
      card.style.setProperty('--ova-spot-y', `${event.clientY - rect.top}px`);
    }

    const tilt = target.closest('[data-ova-tilt]');
    if (tilt) {
      const rect = tilt.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      const strength = parseFloat(tilt.dataset.ovaTilt) || 8;
      tilt.style.setProperty('--ova-tilt-x', `${(-y * strength).toFixed(2)}deg`);
      tilt.style.setProperty('--ova-tilt-y', `${(x * strength).toFixed(2)}deg`);
      tilt.style.setProperty('--ova-glare-x', `${((x + 0.5) * 100).toFixed(1)}%`);
      tilt.style.setProperty('--ova-glare-y', `${((y + 0.5) * 100).toFixed(1)}%`);
    }

    const magnet = target.closest('[data-ova-magnetic]');
    if (magnet) {
      const rect = magnet.getBoundingClientRect();
      const x = event.clientX - (rect.left + rect.width / 2);
      const y = event.clientY - (rect.top + rect.height / 2);
      magnet.style.setProperty('--ova-magnet-x', `${(x * 0.18).toFixed(1)}px`);
      magnet.style.setProperty('--ova-magnet-y', `${(y * 0.28).toFixed(1)}px`);
    }
  }

  document.addEventListener(
    'pointerout',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const related = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      if (!target) return;

      for (const selector of ['[data-ova-tilt]', '[data-ova-magnetic]']) {
        const el = target.closest(selector);
        if (el && !el.contains(related)) {
          el.style.removeProperty('--ova-tilt-x');
          el.style.removeProperty('--ova-tilt-y');
          el.style.removeProperty('--ova-magnet-x');
          el.style.removeProperty('--ova-magnet-y');
        }
      }
    },
    { passive: true }
  );
}

/* ---------- Cart bubble bump ---------- */

function setupCartBump() {
  const bubble = document.querySelector('.cart-bubble');
  const count = bubble?.querySelector('.cart-bubble__text-count');
  if (!bubble || !count || reducedMotion.matches) return;

  let last = count.textContent;
  new MutationObserver(() => {
    if (count.textContent === last) return;
    last = count.textContent;
    bubble.classList.remove('ova-bump');
    void bubble.offsetWidth;
    bubble.classList.add('ova-bump');
  }).observe(bubble, { subtree: true, childList: true, characterData: true });
}

/* ---------- Pause off-screen loops (marquees, auroras) to save battery ---------- */

function setupLoops() {
  const loops = document.querySelectorAll('[data-ova-loop]');
  if (!loops.length) return;
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) entry.target.classList.toggle('is-offscreen', !entry.isIntersecting);
  });
  loops.forEach((el) => observer.observe(el));
}

/* ---------- Boot ---------- */

function init() {
  window.ovaMotionReady = true;
  root.classList.toggle('ova-motion', motionAllowed());
  setupReveal();
  collectParallax();
  onScrollFrame();
  setupPointer();
  setupCartBump();
  setupLoops();

  // Horizon scrolls inside .page-wrapper from 990px up, and the document below that.
  let scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', requestScrollFrame, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', () => {
    scrollTarget.removeEventListener('scroll', requestScrollFrame);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', requestScrollFrame, { passive: true });
    requestScrollFrame();
  });
  window.addEventListener('resize', requestScrollFrame, { passive: true });

  // Hero intro choreography: start once fonts are ready so letters never re-flow mid-animation.
  const intros = document.querySelectorAll('[data-ova-intro]');
  if (intros.length) {
    const start = () => intros.forEach((intro) => intro.classList.add('is-ready'));
    if (!motionAllowed()) start();
    else Promise.race([document.fonts?.ready, new Promise((r) => setTimeout(r, 600))]).then(() => requestAnimationFrame(start));
  }

  reducedMotion.addEventListener('change', () => {
    root.classList.toggle('ova-motion', motionAllowed());
    document.querySelectorAll('[data-ova-reveal]').forEach((el) => el.classList.add('is-revealed'));
    collectParallax();
  });
}

// Theme editor: re-run on section reloads so new markup is wired up (motion stays off there).
document.addEventListener('shopify:section:load', (event) => {
  setupReveal(event.target);
  collectParallax();
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
